/*
 *  Product Form
 *    Try to limit the functions & methods in this class to those that
 *    are related to the primary function of a form - adding a product to the
 *    cart and allowing users to select variants via way of a visual "swatch".
 *    ProductForm can be called as it's own section or instanciated inside
 *    another section's TS.
 *
 *  Version
 *    1.0.0 - 2022/04/10
 */

import { 
  CartAdd, cartAdd, cartGetQuantityOfVariant,
  Variant, printMoney, t
} from '@process-creative/slate-theme-tools';
import { Swatches } from '../../objects/product/swatches';
import { ATTR_DISABLED } from "../../tools/constants/attributes";
import { EVT_VARIANT_CHANGED } from './variant-selector';
import { CLASS_INITIALISED, CLASS_DISABLED, CLASS_HIDDEN, CLASS_ACTIVE } from '../../tools/constants/classes';

import { QuantitySelectorElement } from '../quantity-selector';

export const SEL_FORM_CONTAINER = '[data-product-form]';
const SEL_ADD = '[data-product-form-add]';
const SEL_NOTIFY = '[data-notify]';
const SEL_MONEY = '[data-product-money]';
const SEL_COMPARE = '[data-product-compare-money]';
const SEL_QUANTTIY_SELECTOR = '[data-product-form-qty]';

export class ProductForm {
  container: HTMLFormElement;
  btnAdd: HTMLButtonElement | null = null;
  btnNotify: HTMLButtonElement | null = null;
  qtySelector:QuantitySelectorElement|null = null;
  form: HTMLFormElement | null = null;
  swatches: Swatches | null = null;
  private money: NodeListOf<HTMLSpanElement> | null = null;
  private compare: NodeListOf<HTMLSpanElement> | null = null;

  private adding = false;

  constructor(container: HTMLFormElement) {
    this.container = container;
    this.queryChildren(container);
    
    // Hide notify buttons initially - onVariantChange() will set the correct state
    const notifyButtons = this.container.querySelectorAll<HTMLButtonElement>(SEL_NOTIFY);
    notifyButtons.forEach((btn) => {
      btn.classList.remove(CLASS_ACTIVE);
    });
    this.attachEvents();
  }

  queryChildren(container: HTMLFormElement) {
    this.btnAdd = container.querySelector(SEL_ADD);
    this.btnNotify = container.querySelector(SEL_NOTIFY);
    this.qtySelector = container.querySelector(SEL_QUANTTIY_SELECTOR);
    this.money = container.querySelectorAll<HTMLSpanElement>(SEL_MONEY);
    this.compare = container.querySelectorAll<HTMLSpanElement>(SEL_COMPARE);
  }

  onFormSubmit = async (e: Event) => {
    e.preventDefault();
    e.stopPropagation();

    // Check if a variant is selected
    if (!this.swatches || !this.swatches.getVariant()) {
      alert('Please select a variant before adding to cart');
      return;
    }

    this.adding = true;
    this.btnPending();

    // Build item to add to cart then add
    try {
      const item = Array.from(new FormData(this.container)).reduce((x, y) => {
        return { ...x, [y[0]]: y[1].toString() };
      }, {} as CartAdd['items'][number]);
      item['quantity'] = this.qtySelector ? this.qtySelector.value : 1;
      await cartAdd({ items: [item] });
      this.btnAdded();
      this.onCartSuccessDispatch([item]);
    } catch (e) {
      console.error(e);
      alert(e);
    }

    this.onVariantChange();
    if(this.qtySelector) this.qtySelector.disabled = false;
    this.adding = false;
  }

  onVariantChange() {
    if (!this.swatches) return;
    const variant = this.swatches.getVariant();
    
    if (!variant) {
      this.updateAddToCart(null);
      this.updateNotifyButton(null);
      return;
    }

    const {
      inventory_management, inventory_policy, inventory_quantity,
      price, compare_at_price
    } = variant;
    const inCart = cartGetQuantityOfVariant(variant.id);

    if(this.qtySelector) {
      if(inventory_management === 'shopify' && inventory_policy === 'deny') {
        this.qtySelector.max = (inventory_quantity || 0) - inCart;
      } else {
        this.qtySelector.max = null;
      }
    }

    this.updatePriceElements(price, compare_at_price);
    this.updateAddToCart(variant);
    this.updateNotifyButton(variant);
    this.ensureMutuallyExclusiveButtons(variant);
  }
  
  private ensureMutuallyExclusiveButtons(variant: Variant | null) {
    const notifyButtons = this.container.querySelectorAll<HTMLButtonElement>(SEL_NOTIFY);
    const addToCartVisible = this.btnAdd && !this.btnAdd.classList.contains(CLASS_HIDDEN);
    
    const notifyVisible = Array.from(notifyButtons).some(btn => {
      return btn.classList.contains(CLASS_ACTIVE);
    });
    
    if (addToCartVisible && notifyVisible) {
      if (variant && !variant.available) {
        if (this.btnAdd) {
          this.btnAdd.classList.add(CLASS_HIDDEN);
        }
        notifyButtons.forEach((btn) => {
          btn.classList.add(CLASS_ACTIVE);
        });
      } else {
        if (this.btnAdd) {
          this.btnAdd.classList.remove(CLASS_HIDDEN);
        }
        notifyButtons.forEach((btn) => {
          btn.classList.remove(CLASS_ACTIVE);
        });
      }
    }
  }

  /**
   * Updates elements displaying price based on selected variant
   * @param price variant price
   * @param comparePrice variant compare at price
   */
  updatePriceElements(price: number, comparePrice?: number | null) {
    if(this.money) {
      this.money.forEach(el => el.innerText = printMoney(price));
    }

    if(this.compare) {
      if(comparePrice) {
        this.compare.forEach(el => el.innerText = printMoney(comparePrice));
      } else {
        this.compare.forEach(el => el.innerText = '');
      }
    }
  }

  /**
   * Updates Add to cart button label based on selected variant
   * @param variant - variant object or null
   */
  updateAddToCart(variant: Variant | null) {
    if (!this.btnAdd) return;

    if (!variant) {
      this.btnAdd.innerText = t('products.product.add_to_cart');
      this.btnAdd.classList.add(CLASS_DISABLED);
      this.btnAdd.setAttribute(ATTR_DISABLED, ATTR_DISABLED);
      this.btnAdd.disabled = true;
      this.btnAdd.classList.remove(CLASS_HIDDEN);
      
      // Ensure notify button is hidden when no variant is selected
      const notifyButtons = this.container.querySelectorAll<HTMLButtonElement>(SEL_NOTIFY);
      notifyButtons.forEach((btn) => {
        btn.classList.remove(CLASS_ACTIVE);
      });
      return;
    }

    // Adjust the add to cart button based on QTY available and in cart
    const inCart = cartGetQuantityOfVariant(variant.id);
    const {
      available,
      inventory_policy,
      inventory_management,
      inventory_quantity
    } = variant;

    if (available) {
      if (
        inventory_policy != 'continue' &&
        inventory_management != null &&
        ((inventory_quantity || 0) - inCart) <= 0
      ) {
        this.btnAllInCart();
      } else {
        this.btnAvailable();
      }
    } else {
      this.btnSoldOut();
    }
  }

  /** 
   * Enables button and updates label
   */
  btnAvailable() {
    if(!this.btnAdd) return;
    this.btnAdd.innerText = t('products.product.add_to_cart');
    this.btnAdd.classList.remove(CLASS_DISABLED);
    this.btnAdd.classList.remove('is-disabled');
    this.btnAdd.removeAttribute('disabled');
    this.btnAdd.disabled = false;
    this.btnAdd.classList.remove(CLASS_HIDDEN);
    
    // Ensure notify button is hidden when add to cart is available
    const notifyButtons = this.container.querySelectorAll<HTMLButtonElement>(SEL_NOTIFY);
    notifyButtons.forEach((btn) => {
      btn.classList.remove(CLASS_ACTIVE);
    });
  }

  /**
   * Updates button label if all items are in the cart
   */
  btnAllInCart() {
    if (!this.btnAdd) return;
    this.btnAdd.classList.add(CLASS_DISABLED);
    this.btnAdd.setAttribute(ATTR_DISABLED, ATTR_DISABLED);
    this.btnAdd.innerHTML = t('products.product.all_in_cart');
    this.btnAdd.classList.remove(CLASS_HIDDEN);
    
    // Ensure notify button is hidden when all items are in cart
    const notifyButtons = this.container.querySelectorAll<HTMLButtonElement>(SEL_NOTIFY);
    notifyButtons.forEach(btn => {
      btn.classList.remove(CLASS_ACTIVE);
    });
  }

  /** 
   * Disables ATC if out of stock
   */
  btnSoldOut() {
    if (!this.btnAdd) return;
    this.btnAdd.classList.add(CLASS_DISABLED);
    this.btnAdd.setAttribute(ATTR_DISABLED, ATTR_DISABLED);
    this.btnAdd.innerHTML = t('products.product.sold_out');
    this.btnAdd.classList.add(CLASS_HIDDEN);
    
    // Show notify button when add to cart is sold out
    const notifyButtons = this.container.querySelectorAll<HTMLButtonElement>(SEL_NOTIFY);
    notifyButtons.forEach((btn) => {
      btn.classList.add(CLASS_ACTIVE);
    });
  }

  /**
   * Updates notify button visibility based on variant availability
   * @param variant - variant object or null
   */
  updateNotifyButton(variant: Variant | null) {
    // Find all notify buttons (there might be multiple in the form)
    const notifyButtons = this.container.querySelectorAll<HTMLButtonElement>(SEL_NOTIFY);
    
    if (notifyButtons.length === 0) return;

    if (!variant) {
      // No variant selected - hide all notify buttons, show add to cart (disabled)
      notifyButtons.forEach((btn) => {
        btn.classList.remove(CLASS_ACTIVE);
      });
      if (this.btnAdd) {
        this.btnAdd.classList.remove(CLASS_HIDDEN);
      }
      return;
    }

    if (variant.available) {
      // Variant is available - hide all notify buttons, show add to cart
      notifyButtons.forEach((btn) => {
        btn.classList.remove(CLASS_ACTIVE);
      });
      if (this.btnAdd) {
        this.btnAdd.classList.remove(CLASS_HIDDEN);
      }
    } else {
      // Variant is not available - show notify buttons, hide add to cart
      notifyButtons.forEach((btn) => {
        btn.textContent = 'Notify me when available';
        btn.classList.add(CLASS_ACTIVE);
      });
      if (this.btnAdd) {
        this.btnAdd.classList.add(CLASS_HIDDEN);
      }
    }
  }

  /** 
   * Shows a success state after add to cart
   */
  btnAdded() {
    if (!this.btnAdd) return;
    this.btnAdd.innerHTML = t('products.product.added_to_cart');
    this.btnAdd.classList.remove(CLASS_DISABLED);
    this.btnAdd.disabled = false;
  }

  /** 
   * Updates btn to pending state
   */
  btnPending() {
    if(!this.btnAdd) return;
    this.btnAdd.innerText = t('products.product.cart_pending');
    this.btnAdd.disabled = true;
  }

  attachEvents() {
    this.swatches = new Swatches(this.container, true);
    this.container.addEventListener(EVT_VARIANT_CHANGED, () => this.onVariantChange());
    this.container.addEventListener('submit', this.onFormSubmit);
    this.btnAdd?.addEventListener('click', this.onFormSubmit);

    setTimeout(() => {
      this.onVariantChange();
      setTimeout(() => {
        const variant = this.swatches?.getVariant();
        if (variant) {
          this.ensureMutuallyExclusiveButtons(variant);
        } else {
          this.ensureMutuallyExclusiveButtons(null);
        }
      }, 100);
    }, 0);
  }

  detatchEvents() {
    this.swatches?.dispose();
    this.container.removeEventListener('submit', this.onFormSubmit);
    this.container.removeEventListener(EVT_VARIANT_CHANGED, () => this.onVariantChange());
  }

  // Dispatches a custom event when an item is successfully added to the cart
  private onCartSuccessDispatch(items?: CartAdd['items']) {
    const addToCartEvent = new CustomEvent('cart:item-added', {
      detail: { items }
    });
    window.dispatchEvent(addToCartEvent);
  }
}
